import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, FileText, Upload } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { api, formatErr } from "@/lib/api";

// Convert File → base64 (raw, without data URL prefix)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Erro ao ler o ficheiro."));
    reader.onload = () => {
      const result = reader.result || "";
      const idx = String(result).indexOf(",");
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.readAsDataURL(file);
  });
}

export default function Register() {
  const { content } = useContent();
  const customFields = content.registration_fields || [];

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [extra, setExtra] = useState({});
  const [files, setFiles] = useState({}); // { key: { filename, content_base64, size } }
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const init = {};
    customFields.forEach((f) => { if (f.type !== "file_pdf") init[f.key] = ""; });
    setExtra((cur) => ({ ...init, ...cur }));
  }, [customFields.length]);

  const onPickFile = async (key, ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setErr("");
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setErr("O ficheiro tem de ser um PDF.");
      ev.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr("O ficheiro é demasiado grande (máx. 10 MB).");
      ev.target.value = "";
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setFiles((cur) => ({ ...cur, [key]: { filename: file.name, content_base64: b64, size: file.size } }));
    } catch (er) { setErr(er.message); }
    ev.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        password: form.password,
        extra,
        files,
      });
      setSubmitted(true);
    } catch (er) {
      setErr(formatErr(er.response?.data?.detail) || er.message);
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <main className="max-w-md mx-auto px-6 py-20 text-center" data-testid="page-register-success">
        <CheckCircle2 size={48} className="mx-auto text-[#4A6B53] mb-6" />
        <h1 className="font-serif text-4xl brand-text mb-4">Pedido submetido</h1>
        <p className="brand-muted mb-8">
          O seu registo está a ser analisado pela nossa equipa. Receberá um email a confirmar assim que o pedido for revisto.
        </p>
        <Link to="/" className="btn-primary">Voltar ao início</Link>
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-6 py-20" data-testid="page-register">
      <p className="label-sm mb-3 text-center">Conta</p>
      <h1 className="font-serif text-4xl text-center brand-text mb-4">Criar conta</h1>
      <p className="text-sm brand-muted text-center mb-10">O acesso à loja é sujeito a aprovação pela nossa equipa.</p>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="label-sm">Nome <span style={{ color: "#9E4747" }}>*</span></label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line mt-2" data-testid="reg-name" />
        </div>
        <div>
          <label className="label-sm">Email <span style={{ color: "#9E4747" }}>*</span></label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-line mt-2" data-testid="reg-email" />
        </div>
        <div>
          <label className="label-sm">Palavra-passe <span style={{ color: "#9E4747" }}>*</span></label>
          <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-line mt-2" data-testid="reg-password" />
        </div>

        {customFields.map((f) => (
          <div key={f.key}>
            <label className="label-sm">
              {f.label}{f.required && <span style={{ color: "#9E4747" }}> *</span>}
            </label>
            {f.type === "file_pdf" ? (
              <div className="mt-2">
                {files[f.key] ? (
                  <div className="brand-surface p-3 border brand-border flex items-center gap-3 text-sm" data-testid={`reg-file-${f.key}`}>
                    <FileText size={18} className="brand-primary-text" />
                    <div className="flex-1 min-w-0">
                      <p className="brand-text truncate">{files[f.key].filename}</p>
                      <p className="brand-muted text-xs">{(files[f.key].size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button type="button" onClick={() => setFiles((cur) => { const n = { ...cur }; delete n[f.key]; return n; })} className="text-xs brand-primary-text underline">Remover</button>
                  </div>
                ) : (
                  <label className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed brand-border rounded-sm p-4 cursor-pointer hover:brand-surface text-sm brand-muted" data-testid={`reg-file-drop-${f.key}`}>
                    <Upload size={16} /> Carregar {f.label || "PDF"}
                    <input type="file" accept="application/pdf,.pdf" onChange={(ev) => onPickFile(f.key, ev)} className="hidden" />
                  </label>
                )}
                {f.placeholder && <p className="text-xs brand-muted mt-1">{f.placeholder}</p>}
              </div>
            ) : (
              <input
                type={f.type || "text"}
                required={!!f.required}
                placeholder={f.placeholder || ""}
                value={extra[f.key] || ""}
                onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                className="input-line mt-2"
                data-testid={`reg-extra-${f.key}`}
              />
            )}
          </div>
        ))}

        {err && <p className="text-sm" style={{ color: "#9E4747" }} data-testid="reg-error">{err}</p>}
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="btn-register">
          {loading ? "A enviar..." : "Submeter pedido de registo"}
        </button>
      </form>
      <p className="text-sm text-center brand-muted mt-8">
        Já tem conta? <Link to="/entrar" className="brand-primary-text underline">Entrar</Link>
      </p>
    </main>
  );
}
